import { FC } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Box, Text, useMantineTheme } from '@mantine/core';
import { GrowthDataSet } from '../types/data-set.types';
import { useTranslation } from 'react-i18next';
import { formatAgeLabel } from '../utils/format-age-label';

const COLOR_EDGES = '#e21b37';
const COLOR_MIDDLE = '#f27422';
const COLOR_CENTER = '#00853f';

// -----------------------------------------------------------------------------
interface GrowthChartProps {
  selectedDataset: GrowthDataSet | undefined;
  percentileData: any[];
  patientPoints: any[];
  adjustedPatientPoints: any[];
  showPatientData: boolean;
  showAdjustedData: boolean;
  isWeightForLength: boolean;
  chartLabels: {
    xAxisLabel: string;
    yAxisLabel: string;
    formatXAxisTick: (value: number) => string;
    formatTooltipLabel: (value: number) => string;
  };
  minimizedView?: boolean;
}

// -----------------------------------------------------------------------------
export const GrowthChart: FC<GrowthChartProps> = ({
  selectedDataset,
  percentileData,
  patientPoints,
  adjustedPatientPoints,
  showPatientData,
  showAdjustedData,
  isWeightForLength,
  chartLabels,
  minimizedView = false,
}) => {
  const theme = useMantineTheme();
  const { t } = useTranslation(['patient-pages', 'images-tab']);

  // Mapping for static translation keys
  const staticKeyMap = {
    patientValue: 'growthCharts.patient',
    adjustedValue: 'growthCharts.adjusted',
  } as const;
  type StaticKey = keyof typeof staticKeyMap;

  // Mapping for percentile translation keys
  const percentileKeyMap = {
    p3: 'growthCharts.percentiles.p3',
    p15: 'growthCharts.percentiles.p15',
    p50: 'growthCharts.percentiles.p50',
    p85: 'growthCharts.percentiles.p85',
    p97: 'growthCharts.percentiles.p97',
  } as const;
  type PercentileKey = keyof typeof percentileKeyMap;

  // ---------------------------------------------------------------------------
  const lineColors = {
    p3: COLOR_EDGES,
    p15: COLOR_MIDDLE,
    p50: COLOR_CENTER,
    p85: COLOR_MIDDLE,
    p97: COLOR_EDGES,
    patient: theme.colors.blue[6],
    adjustedPatient: theme.colors.blue[6],
  };

  // ---------------------------------------------------------------------------
  if (!selectedDataset) {
    return (
      <Box p="xl">
        <Text style={{ textAlign: 'center' }}>
          Data for this chart is not yet available.
        </Text>
      </Box>
    );
  }

  // Determine axis labels based on chart type and interval
  let xAxisLabel = '';
  let yAxisLabel = '';
  if (isWeightForLength) {
    xAxisLabel = t('growthCharts.axis.heightCm');
    yAxisLabel = t('growthCharts.axis.weightKg');
  } else if (selectedDataset?.intervalType === 'week') {
    xAxisLabel = t('growthCharts.axis.ageWeeks');
    if (
      selectedDataset.measurementType === 'height' ||
      selectedDataset.measurementType === 'length'
    ) {
      yAxisLabel = t('growthCharts.axis.heightCm');
    } else if (selectedDataset.measurementType === 'weight') {
      yAxisLabel = t('growthCharts.axis.weightKg');
    } else if (selectedDataset.measurementType === 'bmi') {
      yAxisLabel = t('growthCharts.axis.bmi');
    } else if (selectedDataset.measurementType === 'head_circumference') {
      yAxisLabel = t('growthCharts.axis.headCircumferenceCm');
    }
  } else {
    xAxisLabel = t('growthCharts.axis.ageMonths');
    if (
      selectedDataset?.measurementType === 'height' ||
      selectedDataset?.measurementType === 'length'
    ) {
      yAxisLabel = t('growthCharts.axis.heightCm');
    } else if (selectedDataset?.measurementType === 'weight') {
      yAxisLabel = t('growthCharts.axis.weightKg');
    } else if (selectedDataset?.measurementType === 'bmi') {
      yAxisLabel = t('growthCharts.axis.bmi');
    } else if (selectedDataset?.measurementType === 'head_circumference') {
      yAxisLabel = t('growthCharts.axis.headCircumferenceCm');
    }
  }

  // ---------------------------------------------------------------------------
  // Custom label formatter for the tooltip
  function customLabelFormatter(
    value: number,
    payload: any[],
    selectedDataset: any,
    patientPoints: any[],
    adjustedPatientPoints: any[],
    intervalType: string
  ) {
    // Check if the payload includes a patient or adjusted patient point
    let ageObj: { years: number; months: number; days: number } | undefined =
      undefined;
    let isPatient = false;
    let isAdjusted = false;
    for (const p of payload) {
      if (p.dataKey === 'patientValue') {
        const foundPatient = patientPoints.find(
          (pt: any) =>
            pt.age === p.payload.age || pt.length === p.payload.length
        );
        if (foundPatient && p.payload.ageObject) {
          ageObj = p.payload.ageObject;
          console.log(ageObj);
        }
        isPatient = true;
        break;
      } else if (p.dataKey === 'adjustedValue') {
        const foundAdjusted = adjustedPatientPoints.find(
          (pt: any) =>
            pt.age === p.payload.age || pt.length === p.payload.length
        );
        if (foundAdjusted && p.payload.ageObj) {
          ageObj = p.payload.ageObject;
        }
        isAdjusted = true;
        break;
      }
    }
    if (isPatient || isAdjusted) {
      return formatAgeLabel(ageObj, intervalType);
    }
    // Fallback to the old label
    if (selectedDataset?.intervalType === 'week') {
      return `${value} ${
        value > 1
          ? t('weeks', { ns: 'images-tab' })
          : t('week', { ns: 'images-tab' })
      }`;
    }
    if (selectedDataset?.intervalType === 'month') {
      return `${value} ${
        value > 1
          ? t('months', { ns: 'images-tab' })
          : t('month', { ns: 'images-tab' })
      }`;
    }
    return value;
  }

  return (
    <Box h={minimizedView ? '220px' : '500px'}>
      <ResponsiveContainer>
        <LineChart
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 20,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.gray[3]} />
          <XAxis
            dataKey={isWeightForLength ? 'length' : 'age'}
            allowDuplicatedCategory={false}
            type="number"
            domain={['dataMin', 'dataMax']}
            label={{
              value: xAxisLabel,
              position: 'insideBottom',
              offset: -10,
              style: { fill: theme.colors.gray[7] },
            }}
            tick={{ fill: theme.colors.gray[7] }}
            tickFormatter={chartLabels.formatXAxisTick}
          />
          <YAxis
            domain={['auto', 'auto']}
            label={{
              value: yAxisLabel,
              angle: -90,
              position: 'insideLeft',
              style: { fill: theme.colors.gray[7] },
            }}
            tick={{ fill: theme.colors.gray[7] }}
          />
          <Tooltip
            formatter={(value, name) => {
              const nameStr = String(name);
              let label = nameStr;
              if (nameStr in staticKeyMap) {
                label = t(staticKeyMap[nameStr as StaticKey] as any);
              } else if (nameStr in percentileKeyMap) {
                label = t(percentileKeyMap[nameStr as PercentileKey]);
              }
              return [`${value} ${selectedDataset.displayUnit}`, label];
            }}
            labelFormatter={(value, payload) =>
              customLabelFormatter(
                value,
                payload,
                selectedDataset,
                patientPoints,
                adjustedPatientPoints,
                selectedDataset.intervalType || ''
              )
            }
            contentStyle={{
              backgroundColor: theme.white,
              border: `1px solid ${theme.colors.gray[4]}`,
              borderRadius: theme.radius.sm,
              boxShadow: theme.shadows.sm,
            }}
          />
          <Legend
            wrapperStyle={{
              paddingTop: '10px',
            }}
            formatter={(value) => {
              const valueStr = String(value);
              if (valueStr in staticKeyMap) {
                return t(staticKeyMap[valueStr as StaticKey] as any);
              }
              if (valueStr in percentileKeyMap) {
                return t(percentileKeyMap[valueStr as PercentileKey] as any);
              }
              return valueStr;
            }}
          />          {/* Percentile lines */}
          <Line
            data={percentileData}
            type="basis"
            dataKey="p3"
            stroke={lineColors.p3}
            name={t('growthCharts.percentiles.p3')}
            dot={false}
            strokeWidth={1.5}
            isAnimationActive={false}
            connectNulls={true}
          />
          <Line
            data={percentileData}
            type="basis"
            dataKey="p15"
            stroke={lineColors.p15}
            name={t('growthCharts.percentiles.p15')}
            dot={false}
            strokeWidth={1.5}
            isAnimationActive={false}
            connectNulls={true}
          />
          <Line
            data={percentileData}
            type="basis"
            dataKey="p50"
            stroke={lineColors.p50}
            name={t('growthCharts.percentiles.p50')}
            dot={false}
            strokeWidth={2.5}
            isAnimationActive={false}
            connectNulls={true}
          />
          <Line
            data={percentileData}
            type="basis"
            dataKey="p85"
            stroke={lineColors.p85}
            name={t('growthCharts.percentiles.p85')}
            dot={false}
            strokeWidth={1.5}
            isAnimationActive={false}
            connectNulls={true}
          />
          <Line
            data={percentileData}
            type="basis"
            dataKey="p97"
            stroke={lineColors.p97}
            name={t('growthCharts.percentiles.p97')}
            dot={false}
            strokeWidth={1.5}
            isAnimationActive={false}
            connectNulls={true}
          />

          {/* Patient data line - separate from percentile data */}
          {showPatientData && patientPoints.length > 0 && (
            <Line
              data={patientPoints}
              type="basis"
              dataKey="patientValue"
              stroke={lineColors.patient}
              name="patientValue"
              strokeWidth={2.5}
              dot={{ r: 4, strokeWidth: 1 }}
              activeDot={{ r: 6 }}
              isAnimationActive={false}
              connectNulls={true}
            />
          )}

          {/* Adjusted patient data line */}
          {showPatientData &&
            showAdjustedData &&
            adjustedPatientPoints.length > 0 && (
              <Line
                data={adjustedPatientPoints}
                type="basis"
                dataKey="adjustedValue"
                stroke={lineColors.adjustedPatient}
                name="adjustedValue"
                strokeWidth={2.5}
                strokeDasharray="5 5"
                dot={{ r: 4, strokeWidth: 1 }}
                activeDot={{ r: 6 }}
                isAnimationActive={false}
                connectNulls={true}
              />
            )}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};
